import { Box, Flex, Stack, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Dropdown } from "../Dropdown";
import { Modal } from "../Modal";
import MenuMobileCard from "./MenuMobileCard";

import { IconPlus, IconUpgrade } from "@/components/icons";
import { CreateCar } from "@/components/widgets";
import { ChoosePlanModal } from "@/components/widgets/PlanModals";
import { paths } from "@/config/paths";
import { useGarages } from "@/services/garages/get-garages";
import { usePlansUser } from "@/services/plans/get-user-plan";

import type { FC } from "react";

type EditorType = "car-creation" | "garage-creation";

interface MobileNavigationCardProps {
  closeMainModal: () => void;
}

const MobileNavigationCards: FC<MobileNavigationCardProps> = ({ closeMainModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editorType, setEditorType] = useState<EditorType>();
  const [selectedGarageForCarCreation, setSelectedGarageForCarCreation] = useState<string>();
  const { data: plans } = usePlansUser();
  const { data: garages } = useGarages();

  const [showPlanModal, { open: openPlanModal, close: closePlanModal }] = useDisclosure();
  const [opened, { open, close }] = useDisclosure();

  function handleClose() {
    setEditorType(undefined);
    setSelectedGarageForCarCreation(undefined);
    closeMainModal();
    close();
  }

  function openCreateCarModal(garageId?: string) {
    setEditorType("car-creation");
    garageId && setSelectedGarageForCarCreation(garageId);
    open();
  }

  return (
    <>
      <Box px="md">
        <Flex justify="center" align="center" gap="md" mx="0 auto">
          <MenuMobileCard
            title={t("plans.type." + (plans?.name || "starter"))}
            description={t("plans.yourPlan")}
            icon={IconUpgrade}
            bg="secondary.5"
            onClick={openPlanModal}
          />

          {garages && garages?.results?.length > 0 && (
            <MenuMobileCard
              description={t("dashboard.cars.create_button")}
              bg="primary.5"
              icon={IconPlus}
              onClick={garages?.results?.length ? () => openCreateCarModal() : undefined}
            />
          )}
        </Flex>
      </Box>

      {/* Garage/Cars modal */}
      <Modal opened={opened} onClose={handleClose} fullScreen>
        {editorType === "car-creation" && (
          <Stack>
            <Title order={4}>{t("dashboard.cars.select_garage")}</Title>
            <Dropdown
              placeholder={t("dashboard.cars.select_garage_placeholder")}
              data={
                garages?.results.map((garage) => ({
                  value: garage.identifier,
                  label: garage.data.name,
                })) || []
              }
              value={selectedGarageForCarCreation}
              onChange={(value) => setSelectedGarageForCarCreation(value || undefined)}
              required
              searchable
            />
            <Box mt="md">
              {selectedGarageForCarCreation && (
                <CreateCar
                  autoCloseDelay={6000}
                  garageId={selectedGarageForCarCreation}
                  showLessElements={true}
                  onClose={handleClose}
                  onSuccess={() => {
                    navigate(paths.app.garage.root.getHref(selectedGarageForCarCreation));
                    handleClose();
                    closeMainModal();
                  }}
                />
              )}
            </Box>
          </Stack>
        )}
      </Modal>

      {/* Plans modal */}
      <ChoosePlanModal
        isOpen={showPlanModal}
        onClose={() => closePlanModal()}
        title={t("plans.planSectionTitle")}
      />
    </>
  );
};

export default MobileNavigationCards;
